import { useMemo, type ReactNode } from "react";

import type { CuratedPlaceOfSafety } from "@thalamos/common";
import { CuratedPlaceOfSafetySchema } from "@thalamos/common";

import { apiHooks } from "../../../api/index.js";
import { PlacesOfSafetyContext } from "./PlaceOfSafetyContext.js";

/**
 * Fetches the curated places of safety once and shares them via context for
 * the whole browsing session.
 *
 * Mounted high in the authenticated app shell (see RootComponent) so the fetch
 * runs a single time when the session starts and the result is held in memory
 * for the rest of the session. Police devices are locked down and may not
 * expose persistent storage, so this is deliberately in-memory only — no
 * sessionStorage / localStorage.
 *
 * Previously every place-of-safety form page fetched the list afresh on mount
 * with HTTP caching disabled, so a slow or dropped connection left the picker
 * empty and nudged officers into manual free-text entry. Preloading once,
 * early, removes that trap.
 */
export const PlacesOfSafetyProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [{ data, loading, error }, refetch] = apiHooks.placeOfSafety.getAll();

  const value = useMemo(
    () => ({
      placesOfSafety: (data?.placesOfSafety ?? [])
        .map((placeOfSafety) => {
          const result = CuratedPlaceOfSafetySchema.safeParse(placeOfSafety);
          return result.success ? result.data : undefined;
        })
        .filter(
          (placeOfSafety): placeOfSafety is CuratedPlaceOfSafety =>
            placeOfSafety !== undefined,
        ),
      isLoading: loading,
      // Surface the failure so consumers can show an explicit error state
      // instead of an empty list that looks indistinguishable from "no places".
      isError: error !== undefined && error !== null,
      // Allow the UI to retry after a transient failure without a full reload.
      refetch: () => {
        void refetch();
      },
    }),
    [data, loading, error, refetch],
  );

  return (
    <PlacesOfSafetyContext.Provider value={value}>
      {children}
    </PlacesOfSafetyContext.Provider>
  );
};
